import React from "react";
import Button from "components/Button/Regular";
import Card from "components/Card/Card";
import { ReactComponent as PlusIcon } from "assets/icons/plus.svg";
import { useNavigate } from "react-router-dom";
import Page from "components/Page/Page";
import Table from "components/Table/Table";
import { PaginationBar } from "components/Pagination";
import PageSizeSelect from "components/Table/PageSizeSelect";
import { useTableView } from "components/Table/useTableView";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import { ClinicRow, getClinicsList } from "services/lists.service";
import { deleteOrganisation } from "services/clinics.service";

const ClinicsList = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();
  const { orgId, orgName, setActiveOrg, refreshOrganisations, loading: orgContextLoading } =
    useActiveOrg();
  const [clinics, setClinics] = React.useState<ClinicRow[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  // The status column is rendered from the active-org id rather than the row, so
  // it needs a key present on every row for the column to exist. Memoised so the
  // table view is not handed a new array identity on each render.
  const clinicRows = React.useMemo(
    () => clinics.map((c) => ({ ...c, status: "" })),
    [clinics]
  );
  const view = useTableView(clinicRows);

  const load = React.useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const { data, error: apiError } = await getClinicsList(token);
      if (apiError) {
        setError(apiError.message);
        setClinics([]);
        return;
      }
      setClinics(data);
    } catch (e) {
      const err = e as { error?: string; message?: string };
      const isAuthError =
        err.error === "login_required" ||
        err.error === "consent_required" ||
        err.message?.includes("Missing Refresh Token") ||
        err.message?.includes("login_required");
      if (isAuthError) {
        loginWithRedirect({ appState: { returnTo: window.location.pathname } });
        return;
      }
      setError((e as Error).message);
      setClinics([]);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, loginWithRedirect]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id: string) => {
    if (
      !window.confirm(
        "Delete this organisation? This is destructive. Continue?"
      )
    ) {
      return;
    }
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = await deleteOrganisation(token, Number(id));
      if (apiError) {
        setError(apiError.message);
        return;
      }
      if (String(orgId) === id) {
        window.location.reload();
        return;
      }
      await Promise.all([load(), refreshOrganisations()]);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const busy = loading || orgContextLoading;
  const showEmptyRequired = !busy && clinics.length === 0 && !error;
  const showTable = !busy && clinics.length > 0;
  const showErrorOnly = !busy && error && clinics.length === 0;

  return (
    <Page
      title="Providers"
      subTitle={
        orgId > 0
          ? `Active provider: ${orgName ?? orgId}`
          : "Choose an active provider from the header"
      }
      actions={
        <Button onClick={() => navigate("/app/clinics/new-clinic")}>
          <PlusIcon />
          <span>Create New Provider</span>
        </Button>
      }
    >
      {busy && (
        <p className="text-sm text-slate-500">Loading providers…</p>
      )}

      {showEmptyRequired && (
        <Card title="No provider yet" className="max-w-2xl mx-auto">
          <div className="p-8 text-center text-slate-600">
            <p className="text-lg mb-2 font-medium text-slate-800">
              A provider is required to use Funely
            </p>
            <p className="mb-6 text-sm leading-relaxed">
              You do not belong to any organisation yet. Create a clinic to
              manage patients, clients, services, and staff. Until then, other
              areas of the app stay unavailable.
            </p>
            <Button
              color="Primary"
              type="button"
              onClick={() => navigate("/app/clinics/new-clinic")}
            >
              <PlusIcon />
              <span>Create your first provider</span>
            </Button>
          </div>
        </Card>
      )}

      {showErrorOnly && (
        <Card title="Could not load providers" className="max-w-2xl mx-auto">
          <p className="p-6 text-sm text-red-600">{error}</p>
        </Card>
      )}

      {showTable && (
        <>
          <PageSizeSelect
            value={view.pageSize}
            onChange={view.setPageSize}
            className="mb-3"
          />

          <Table<{
            id: string;
            name: string;
          }>
            loading={loading}
            columns={{
              id: "Provider Id",
              name: "Provider Name",
              status: "Status",
            }}
            rows={view.pageRows}
            sort={view.sort}
            onSortChange={view.toggleSort}
            renderCell={(colId, row) => {
              if (colId !== "status") return undefined;
              const isActive = String(orgId) === row.id;
              return isActive ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-vg-blue/10 text-vg-blue">
                  <span className="h-1.5 w-1.5 rounded-full bg-vg-blue" />
                  Active
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setActiveOrg(Number(row.id), row.name)}
                  className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 transition-colors"
                >
                  Set active
                </button>
              );
            }}
            handleViewItem={(id) => navigate(`/app/clinics/${id}`)}
            handleRemoveItem={handleDelete}
          />

          <div className="mt-4">
            <PaginationBar
              from={view.from}
              to={view.to}
              total={view.total}
              currentPage={view.page}
              totalPages={view.totalPages}
              onPrev={view.goPrev}
              onNext={view.goNext}
              onPageSelect={view.goToPage}
            />
          </div>
        </>
      )}
    </Page>
  );
};

export default ClinicsList;
